import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  CheckSquare,
  Megaphone,
  LogOut,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_ristwatch-crm/artifacts/304sorwq_logosd.png";

const OPS = [
  { to: "/operations/daily", label: "Daily To-Do", color: "var(--rw-dot-todo)" },
  { to: "/operations/pipeline", label: "Pipeline Report", color: "var(--rw-dot-pipeline)" },
  { to: "/operations/calling", label: "YTD Calling Activity", color: "var(--rw-dot-calling)" },
  { to: "/operations/intros", label: "Professional Introductions", color: "var(--rw-dot-intros)" },
  { to: "/operations/schedule", label: "Monthly Call Schedule", color: "var(--rw-dot-schedule)" },
];

const CONTACTS_TABS = [
  { to: "/contacts/coi", label: "Centers of Influence", color: "#E54D2E" },
  { to: "/contacts/prospects", label: "Prospects", color: "#F76B15" },
  { to: "/contacts/linkedin", label: "LinkedIn", color: "#0067B8" },
];

const linkBase =
  "flex items-center gap-3 px-4 py-2 text-[13px] transition-colors rounded-[4px] mx-2";

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!user || !user.is_demo) return;
    const wipe = () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/demo/end`;
        navigator.sendBeacon(url);
      } catch {}
    };
    window.addEventListener("pagehide", wipe);
    return () => window.removeEventListener("pagehide", wipe);
  }, [user]);

  return (
    <div className="flex min-h-screen">
      {user?.is_demo && (
        <div
          className="fixed top-0 left-0 right-0 z-50 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs px-4 py-2 flex items-center justify-center gap-3 flex-wrap"
          data-testid="demo-banner"
        >
          <span className="font-semibold uppercase tracking-widest">Demo Mode</span>
          <span>You're using a temporary account. Your data will be wiped when you leave or after 24 hours.</span>
          <button
            onClick={async () => {
              if (!window.confirm("Reset all demo data and reload sample records?")) return;
              try {
                await api.post("/auth/demo/reset");
                toast.success("Demo data reset");
                window.location.reload();
              } catch (e) {
                toast.error(e.response?.data?.detail || e.message);
              }
            }}
            className="underline font-semibold ml-2"
            data-testid="reset-demo-btn"
          >
            Reset demo data
          </button>
          <button
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="underline font-semibold"
            data-testid="exit-demo-btn"
          >
            Exit demo
          </button>
        </div>
      )}

      <aside className="rw-sidebar w-64 fixed h-screen flex flex-col" data-testid="sidebar">
        <div className="px-4 pt-5 pb-4 flex items-center justify-start border-b border-white/5" data-testid="sidebar-brand">
          <img src={LOGO} alt="RistWatch" className="h-12 w-auto" />
        </div>

        <nav className="mt-4 flex-1 overflow-y-auto pb-4 space-y-0.5">
          <NavLink to="/" end className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`} data-testid="nav-dashboard">
            <LayoutDashboard size={16} />
            <span>Dashboard</span>
          </NavLink>

          <div className="label-eyebrow !text-white/40 px-6 mt-5 mb-2">Operations</div>
          {OPS.map((o) => (
            <NavLink
              key={o.to}
              to={o.to}
              className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`}
              data-testid={`nav-${o.to.split("/").pop()}`}
            >
              <span className="nav-dot" style={{ background: o.color }} />
              <span>{o.label}</span>
            </NavLink>
          ))}

          <div className="label-eyebrow !text-white/40 px-6 mt-5 mb-2">Contacts</div>
          {CONTACTS_TABS.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`}
              data-testid={`nav-contacts-${c.to.split("/").pop()}`}
            >
              <span className="nav-dot" style={{ background: c.color }} />
              <span>{c.label}</span>
            </NavLink>
          ))}
          <NavLink to="/networking" className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`} data-testid="nav-networking">
            <span className="nav-dot" style={{ background: "#8E4EC6" }} />
            <span>Networking</span>
          </NavLink>

          <div className="label-eyebrow !text-white/40 px-6 mt-5 mb-2">Manage</div>
          <NavLink to="/expenses" className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`} data-testid="nav-expenses">
            <Wallet size={16} />
            <span>Expenses</span>
          </NavLink>
          <NavLink to="/follow-ups" className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`} data-testid="nav-followups">
            <CheckSquare size={16} />
            <span>Follow-Ups</span>
          </NavLink>
          <NavLink to="/marketing" className={({ isActive }) => `${linkBase} ${isActive ? "active" : ""}`} data-testid="nav-marketing">
            <Megaphone size={16} />
            <span>Marketing</span>
          </NavLink>
        </nav>

        <div className="border-t border-white/5 p-4 flex items-center gap-3" data-testid="sidebar-user">
          <div className="h-8 w-8 rounded-[4px] bg-white/10 grid place-items-center text-xs font-semibold uppercase">
            {(user?.name || user?.email || "?").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] truncate">{user?.name || user?.email}</div>
            <div className="label-eyebrow !text-white/40">{user?.email}</div>
          </div>
          <button
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="text-white/60 hover:text-white"
            data-testid="logout-btn"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      <main className={`ml-64 flex-1 min-h-screen bg-[var(--rw-bg)] ${user?.is_demo ? "pt-8" : ""}`}>
        <Outlet />
      </main>
    </div>
  );
}
